import { useEffect, useMemo, useState } from "react";
import { IcLocation } from "@racoon/icons";
import { Button, Popover, PopoverContent, PopoverTrigger, Sheet, SheetContent } from "@racoon/ui";
import { ChevronDown, X } from "lucide-react";
import { Namespace, useT } from "@racoon/i18n";

export type DropdownSelectorOption = {
  value: string;
  label: string;
};

type CommonProps = {
  options: DropdownSelectorOption[];
  triggerLabel: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  menuClassName?: string;
  placeholderTitle?: string;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: string;
  onApply: (value: string) => void;
  allOptionLabel?: string;
  allOptionValue?: string;
};

type MultiProps = CommonProps & {
  multiple: true;
  values: string[];
  onApply: (values: string[]) => void;
  sectionTitle: string;
  emptyMessage: string;
  resetLabel: string;
};

type DropdownSelectorProps = SingleProps | MultiProps;

export function DropdownSelector({
  options,
  triggerLabel,
  open,
  onOpenChange,
  menuClassName,
  placeholderTitle,
  ...props
}: DropdownSelectorProps) {
  const t = useT();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const [isMobile, setIsMobile] = useState(false);
  const [draftValues, setDraftValues] = useState<string[]>([]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px), (pointer: coarse)");
    const handleChange = () => setIsMobile(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  const committedValues = useMemo(() => {
    if (props.multiple) return props.values;
    return [props.value];
  }, [props.multiple, props.multiple ? props.values : props.value]);

  useEffect(() => {
    if (isOpen) {
      setDraftValues(committedValues);
    }
  }, [isOpen, committedValues]);

  const applyDraft = () => {
    if (props.multiple) {
      props.onApply(draftValues);
    } else {
      props.onApply(draftValues[0] ?? props.allOptionValue ?? "");
    }
    setIsOpen(false);
  };

  const MobileOptionRow = ({
    label,
    isSelected,
    onClick,
  }: {
    label: string;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-2xl p-2 text-left transition-colors ${
        isSelected ? "bg-brand-blue text-white" : "bg-white text-slate-900 hover:bg-gray-50"
      }`}
    >
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-brand-blue bg-white">
        {isSelected ? <span className="size-2 rounded-full bg-brand-blue" /> : null}
      </span>
      <span className="text-sm/[14px]">{label}</span>
    </button>
  );

  const singleOptionsWithAll = useMemo(() => {
    if (props.multiple) return options;
    if (!props.allOptionLabel) return options;
    return [{ value: props.allOptionValue ?? "all", label: props.allOptionLabel }, ...options];
  }, [options, props]);

  const renderTrigger = (popoverTrigger = false) => {
    const triggerClass = popoverTrigger
      ? "w-full justify-between gap-2 bg-white !p-2"
      : "w-full flex items-center justify-between px-2 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors";

    const inner = (
      <>
        <div className="flex items-center gap-1 min-w-0 flex-1 text-left">
          <IcLocation className="h-5 w-5 shrink-0 text-brand-blue" />
          <span className="text-sm font-normal text-gray-900 truncate">{triggerLabel}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </>
    );

    if (popoverTrigger) {
      return (
        <Button variant="outline" className={triggerClass} size="sm" type="button">
          {inner}
        </Button>
      );
    }

    return (
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={triggerClass}>
        {inner}
      </button>
    );
  };

  const renderMultiDesktopOptionRow = (option: DropdownSelectorOption) => {
    const selected = draftValues.includes(option.value);
    return (
      <button
        key={option.value}
        type="button"
        onClick={() =>
          setDraftValues((prev) =>
            prev.includes(option.value)
              ? prev.filter((v) => v !== option.value)
              : [...prev, option.value],
          )
        }
        className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
          selected
            ? "bg-brand-blue-light text-brand-blue"
            : "bg-white text-slate-900 hover:bg-gray-50"
        }`}
      >
        <span
          className={`flex size-5 shrink-0 items-center justify-center rounded-full border ${
            selected ? "border-brand-blue bg-brand-blue" : "border-slate-300 bg-white"
          }`}
        >
          {selected ? <span className="size-2 rounded-full bg-white" /> : null}
        </span>
        <span className="text-sm font-medium">{option.label}</span>
      </button>
    );
  };

  const renderContentBody = () => {
    if (isMobile) {
      if (props.multiple) {
        if (options.length === 0)
          return <p className="text-sm text-text-secondary">{props.emptyMessage}</p>;
        return (
          <div className="space-y-2">
            {options.map((option) => (
              <MobileOptionRow
                key={option.value}
                label={option.label}
                isSelected={draftValues.includes(option.value)}
                onClick={() =>
                  setDraftValues((prev) =>
                    prev.includes(option.value)
                      ? prev.filter((v) => v !== option.value)
                      : [...prev, option.value],
                  )
                }
              />
            ))}
          </div>
        );
      }

      return (
        <div className="space-y-1">
          {singleOptionsWithAll.map((option) => (
            <MobileOptionRow
              key={option.value}
              label={option.label}
              isSelected={draftValues[0] === option.value}
              onClick={() => setDraftValues([option.value])}
            />
          ))}
        </div>
      );
    }

    if (props.multiple) {
      if (options.length === 0)
        return <p className="text-sm text-text-secondary">{props.emptyMessage}</p>;
      return <div className="space-y-2">{options.map(renderMultiDesktopOptionRow)}</div>;
    }

    return (
      <div className="space-y-1">
        {singleOptionsWithAll.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              props.onApply(option.value);
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm font-normal text-gray-900"
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  };

  const renderFooter = () => {
    // <div className="p-4 border-t border-slate-200"></div>
    if (!isMobile && !props.multiple) return null;

    if (props.multiple) {
      return (
        <div className={isMobile ? "flex gap-2 mb-2 p-4 border-t border-slate-200" : "flex gap-2"}>
          <Button
            variant="secondary"
            size={isMobile ? undefined : "sm"}
            className="flex-1"
            type="button"
            onClick={() => setDraftValues([])}
          >
            {props.resetLabel}
          </Button>
          <Button
            size={isMobile ? undefined : "sm"}
            className="flex-1"
            type="button"
            onClick={applyDraft}
          >
            {t(Namespace.Common, "apply") || "Применить"}
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {isMobile ? (
        <>
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="w-full flex items-center justify-between px-2 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-1">
              <IcLocation className="h-5 w-5 shrink-0 text-brand-blue" />
              <span className="text-sm font-normal text-gray-900">{triggerLabel}</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
              side="bottom"
              className="p-0 w-full rounded-t-2xl bg-white border border-gray-200"
              aria-describedby={undefined}
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h3 className="text-base font-semibold">
                  {props.multiple
                    ? props.sectionTitle
                    : placeholderTitle || t(Namespace.Common, "select") || "Выберите"}
                </h3>
                <div className="flex items-center gap-2">
                  {!props.multiple && (
                    <Button size="sm" type="button" onClick={applyDraft}>
                      {t(Namespace.Common, "apply") || "Применить"}
                    </Button>
                  )}
                  <button
                    type="button"
                    className="flex size-6 items-center justify-center rounded-full bg-slate-900 text-white"
                    onClick={() => setIsOpen(false)}
                    aria-label={t(Namespace.Govdash, "actions.close") || "Закрыть"}
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>
              <div className="max-h-[60vh] overflow-y-auto p-3">{renderContentBody()}</div>
              {renderFooter()}
            </SheetContent>
          </Sheet>
        </>
      ) : props.multiple ? (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <div className="border-slate-200">
            <PopoverTrigger asChild>{renderTrigger(true)}</PopoverTrigger>
          </div>
          <PopoverContent
            align="start"
            className="w-[min(var(--radix-popover-trigger-width),calc(100vw-1rem))] max-h-[min(60vh,20rem)] overflow-y-auto p-3"
          >
            <div className="space-y-3">
              <div className="text-sm font-medium">{props.sectionTitle}</div>
              {renderContentBody()}
              {renderFooter()}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <div className="w-full">
            <PopoverTrigger asChild>{renderTrigger(false)}</PopoverTrigger>
          </div>
          <PopoverContent
            align="start"
            className={`w-[min(var(--radix-popover-trigger-width),calc(100vw-1rem))] max-h-[min(60vh,20rem)] overflow-y-auto p-0 ${menuClassName ?? ""}`}
          >
            {renderContentBody()}
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
